/**
 * "Is this week finished?" — ONE definition, read by three surfaces.
 *
 * The week summary's *Days so far* card, the submit dialog's completeness guard
 * and the approval bar all have to answer the same question, and they must
 * answer it the same way. Before this module the summary card computed it
 * inline and the submit dialog did not ask at all: the app rendered
 * "27.00 to go" in amber six inches under a Submit button that shipped the week
 * without a word. A financial app that knows a timesheet is a third full and
 * stays quiet at the moment of commitment is worse than one that never knew.
 *
 * ⚠ It is a COMPLETENESS check, not a presence check. `daysTracked` (one entry
 * of any size counts a day as done) is exactly the signal that flipped a whole
 * week green on the strength of a 36-second timer, so nothing here reads it:
 * a weekday is short when it holds less than the person's contracted DAY, and
 * the week is short when it holds less than their contracted week.
 */

import type { WeekData } from './api';
import { weekCapacity } from './capacity';
import { formatHoursUnit, todayIso, weekdayLabel, type HoursFormat } from './hours';

export interface DayGap {
  date: string;
  /** Mon / Tue / … — the label the chips and the sentence both print. */
  label: string;
  hours: number;
  /** Nothing at all, versus something under the contracted day. */
  kind: 'blank' | 'short';
}

export interface WeekCompleteness {
  /** Contracted hours per week, or null when the person has no target. */
  target: number | null;
  /** Contracted hours per working day (weekly ÷ 5), or null. */
  dailyTarget: number | null;
  /** "40 h" — empty string when there is no target. */
  targetLabel: string;
  /** Hours in the week, including anything passed as `extraHours`. */
  total: number;
  /** Hours still to log against the target (0 when met or no target). */
  remaining: number;
  /** Hours beyond the target. */
  over: number;
  /** The week is entirely in the future. */
  notStarted: boolean;
  /** Weekdays BEFORE today holding nothing. */
  blank: string[];
  /**
   * Weekdays before today holding *something* but less than a contracted day.
   * Empty when the person has no target — "short of nothing" is not a fact.
   */
  short: string[];
  /** Blank + short, in calendar order, with labels. */
  gaps: DayGap[];
  /** Today, a weekday, still blank — reported as state, never as a problem. */
  todayOpen: boolean;
  /** Nothing to warn about: no target missed and no earlier weekday empty. */
  complete: boolean;
}

function isWeekday(day: string): boolean {
  const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
  return dow !== 0 && dow !== 6;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

export interface CompletenessInput {
  /**
   * Hours the screen is showing but the server has not saved yet (a cell mid-
   * edit). Counted toward the week, so the guard never contradicts the figure
   * printed above it.
   */
  extraHours?: number;
  /**
   * Seconds a timer has been running TODAY, in hours. Counted onto today
   * specifically: a clock that is visibly ticking on the screen used to leave
   * the card reading "Today — nothing yet", which is the card calling the user
   * a slacker while they work.
   */
  runningToday?: number;
}

export function weekCompleteness(
  week: WeekData,
  { extraHours = 0, runningToday = 0 }: CompletenessInput = {},
): WeekCompleteness {
  const today = todayIso();
  const capacity = weekCapacity(week.person);
  const total = Math.max(0, r2(week.weekTotal + extraHours + runningToday));
  const target = capacity.weekly;
  const remaining = target === null ? 0 : Math.max(0, r2(target - total));
  const over = target === null ? 0 : Math.max(0, r2(total - target));

  const notStarted = week.days[0] > today;
  const past = week.days.filter((day) => day < today && isWeekday(day));
  const hoursOn = (day: string) =>
    (week.dayTotals[day] ?? 0) + (day === today ? runningToday : 0);

  const blank = past.filter((day) => hoursOn(day) === 0);
  const short =
    capacity.daily === null
      ? []
      : past.filter((day) => hoursOn(day) > 0 && hoursOn(day) < capacity.daily!);

  const gaps: DayGap[] = past
    .filter((day) => blank.includes(day) || short.includes(day))
    .map((day) => ({
      date: day,
      label: weekdayLabel(day),
      hours: hoursOn(day),
      kind: hoursOn(day) === 0 ? ('blank' as const) : ('short' as const),
    }));

  const todayOpen = week.days.includes(today) && isWeekday(today) && hoursOn(today) === 0;

  return {
    target,
    dailyTarget: capacity.daily,
    targetLabel: capacity.weeklyLabel,
    total,
    remaining,
    over,
    notStarted,
    blank,
    short,
    gaps,
    todayOpen,
    complete: !notStarted && blank.length === 0 && remaining === 0,
  };
}

/**
 * The one-line summary a submit dialog leads with — what the app already knows
 * and used to keep to itself. `null` when there is genuinely nothing to say.
 */
export function completenessWarning(
  c: WeekCompleteness,
  /**
   * The unit the sheet behind the dialog is showing. "27.00 h short" printed
   * over a grid reading 27:00 is the same two-formats-for-one-number failure
   * the status bar had.
   */
  format: HoursFormat = 'decimal',
): string | null {
  const parts: string[] = [];
  if (c.target !== null && c.remaining > 0) {
    parts.push(`${formatHoursUnit(c.remaining, format)} short of the ${c.targetLabel} week`);
  }
  if (c.blank.length > 0) {
    const names = c.blank.map((d) => weekdayLabel(d)).join(', ');
    parts.push(`${names} ${c.blank.length === 1 ? 'holds' : 'hold'} nothing`);
  }
  if (parts.length === 0) return null;
  return parts.join(' · ');
}
